/**
 * Findings period: the time part of the Findings page's global scope
 * (queue, hotspots, pages). The wire shape carries tokens, not instants,
 * so a saved "last 30 days" stays relative; the server resolves them
 * against `now()`, the viewer's time zone and the build's first run.
 */
import { z } from "zod";

/** A calendar date, `YYYY-MM-DD`. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const findingsPeriodSchema = z.discriminatedUnion("kind", [
  /** The last N days, up to now. */
  z.object({
    kind: z.literal("last"),
    days: z.number().int().min(1).max(3650),
  }),
  /** From the start of `since` to the end of `until` (open-ended without it). */
  z.object({
    kind: z.literal("range"),
    since: isoDate,
    until: isoDate.optional(),
  }),
  /** From the first run of a build. */
  z.object({ kind: z.literal("sinceBuild"), buildId: z.string().uuid() }),
]);
export type FindingsPeriod = z.infer<typeof findingsPeriodSchema>;

/**
 * `seen`: still happening — any observation from a run in the period.
 * `new`: first seen in the period (nothing observed before it).
 */
export const FINDINGS_PERIOD_MODES = ["seen", "new"] as const;
export type FindingsPeriodMode = (typeof FINDINGS_PERIOD_MODES)[number];

/**
 * The period summary's buckets ("did it help?"), each also a queue
 * filter (`periodBucket`). `not_seen` is absence of an observation in a
 * re-run, never "fixed": only a re-run that resolves a finding verifies it.
 *
 * - `new`: observed in the period, never before it.
 * - `still`: observed in the period and before it.
 * - `not_seen`: observed before, unresolved, not observed in the period
 *   although a completed run of one of its scenarios started in it.
 * - `not_rerun`: as `not_seen`, but none of its scenarios ran in the period.
 * - `verified` / `regressed`: moved there in the period.
 */
export const FINDINGS_PERIOD_BUCKETS = [
  "new",
  "still",
  "not_seen",
  "not_rerun",
  "verified",
  "regressed",
] as const;
export type FindingsPeriodBucket = (typeof FINDINGS_PERIOD_BUCKETS)[number];

/** Per-bucket canonical counts for a period. */
export type FindingsPeriodSummary = Record<FindingsPeriodBucket, number>;

function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** IANA zone calendar dates resolve in; invalid zones are rejected. */
export const timeZoneSchema = z.string().max(64).refine(isTimeZone, {
  message: "Unknown time zone",
});
